<template>
  <div class="login-container">
    <div class="login-content">
      <div class="logo">
        <img src="@/assets/logotipo-short.webp" alt="Coppados">
      </div>
      
      <h1>Admin Login</h1>
      <p class="subtitle">Acceso reservado para administradores</p>

      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <div v-if="!isAdmin && user" class="error-message">
        Este email no tiene permisos de administrador
      </div>

      <form @submit.prevent="handleLogin" class="login-form">
        <div class="input-group">
          <input
            v-model="email"
            type="email"
            placeholder="Email"
            required
            :disabled="loading"
          >
        </div>

        <div class="input-group">
          <input
            v-model="password"
            type="password"
            placeholder="Contraseña"
            required
            :disabled="loading"
          >
        </div>

        <button 
          type="submit" 
          class="login-button"
          :disabled="loading || isCheckingAdmin"
        >
          <span v-if="!loading && !isCheckingAdmin">Ingresar</span>
          <span v-else>Verificando...</span>
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { signInWithEmailAndPassword, signInWithPopup, OAuthProvider } from 'firebase/auth'
import { auth } from '@/services/firebase'
import { validateAdminEmail } from '@/services/adminService'

export default {
  name: 'LoginForm',
  data() {
    return {
      email: '',
      password: '',
      loading: false,
      isCheckingAdmin: false,
      error: null,
      user: null,
      isAdmin: false,
      adminEmail: process.env.VUE_APP_ADMIN_EMAIL
    }
  },
  methods: {
    async handleLogin() {
      this.loading = true
      this.error = null

      try {
        // Validar que el email coincida con el del .env
        const validation = validateAdminEmail(this.email, this.adminEmail)
        if (!validation.valid) {
          this.error = validation.error
          return
        }

        this.isCheckingAdmin = true

        const userCredential = await signInWithEmailAndPassword(auth, this.email, this.password)
        this.user = userCredential.user
        
        // Verificación adicional
        const finalValidation = validateAdminEmail(this.user.email, this.adminEmail)
        if (!finalValidation.valid) {
          await auth.signOut()
          this.error = finalValidation.error
          this.user = null
          return
        }

        this.isAdmin = true
        this.$emit('login-success')
      } catch (error) {
        console.error('Error:', error)
        this.error = this.getErrorMessage(error.code)
        this.user = null
      } finally {
        this.loading = false
        this.isCheckingAdmin = false
      }
    },

    async handleAppleLogin() {
      this.loading = true
      this.error = null
      this.isCheckingAdmin = true

      try {
        const provider = new OAuthProvider('apple.com')
        provider.addScope('email')
        provider.addScope('name')
        
        const userCredential = await signInWithPopup(auth, provider)
        this.user = userCredential.user

        // Validar email
        const validation = validateAdminEmail(this.user.email, this.adminEmail)
        if (!validation.valid) {
          await auth.signOut()
          this.error = validation.error
          this.user = null
          return
        }

        this.isAdmin = true
        this.$emit('login-success')
      } catch (error) {
        console.error('Error:', error)
        this.error = this.getErrorMessage(error.code)
        this.user = null
      } finally {
        this.loading = false
        this.isCheckingAdmin = false
      }
    },

    getErrorMessage(code) {
      const messages = {
        'auth/invalid-email': 'Email inválido',
        'auth/user-disabled': 'Usuario deshabilitado',
        'auth/user-not-found': 'Usuario no encontrado',
        'auth/wrong-password': 'Contraseña incorrecta',
        'auth/too-many-requests': 'Demasiados intentos. Intenta más tarde',
        'auth/popup-closed-by-user': 'Inicio de sesión cancelado',
        'auth/operation-not-allowed': 'Operación no permitida'
      }
      return messages[code] || 'Error al iniciar sesión'
    }
  }
}
</script>

<style scoped>
.login-container {
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #000000 0%, #1a1a1a 100%);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.login-content {
  width: 100%;
  max-width: 380px;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.logo {
  text-align: center;
  margin-bottom: 2rem;
}

.logo img {
  width: 60px;
  height: 60px;
  object-fit: contain;
}

h1 {
  font-size: 2rem;
  color: white;
  margin-bottom: 0.5rem;
  text-align: center;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.subtitle {
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  font-size: 0.95rem;
  margin-bottom: 2rem;
  font-weight: 400;
}

.error-message {
  background: rgba(255, 59, 48, 0.1);
  border: 1px solid rgba(255, 59, 48, 0.3);
  color: #ff3b30;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  text-align: center;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.input-group {
  position: relative;
}

input {
  width: 100%;
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  font-family: inherit;
}

input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

input:focus {
  outline: none;
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.3);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.05);
}

input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.login-button {
  padding: 0.875rem 1rem;
  background: white;
  border: none;
  border-radius: 12px;
  color: black;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
}

.login-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.95);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(255, 255, 255, 0.15);
}

.login-button:active:not(:disabled) {
  transform: translateY(0);
}

.login-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.divider {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1.5rem 0;
  color: rgba(255, 255, 255, 0.4);
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.2);
}

.apple-button {
  width: 100%;
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  letter-spacing: 0.5px;
}

.apple-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.apple-button:active:not(:disabled) {
  background: rgba(255, 255, 255, 0.2);
}

.apple-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.apple-icon {
  width: 20px;
  height: 20px;
}

@media (max-width: 480px) {
  .login-content {
    max-width: 100%;
    border-radius: 0;
    padding: 1.5rem 1rem;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  h1 {
    font-size: 1.75rem;
  }

  .logo {
    margin-bottom: 1.5rem;
  }
}
</style>
